import * as React from 'react';
import dayjs from 'dayjs';
import {
    Alert,
    Box,
    Chip,
    LinearProgress,
    Paper,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import {useQuery} from '@tanstack/react-query';
import {getBoardStatsQuery} from '../../services/taskService';
import {BoardStats} from '../../types/task';
import {SettingsCard} from '../layouts/SettingsCard';
import {queryKeys} from '../../data/queryKeys';
import {completionsPerDay, percent} from '../../utilities/stats';

const RANGES = [
    {days: 7, label: '7 days'},
    {days: 30, label: '30 days'},
    {days: 90, label: '90 days'},
];

type StatsViewProps = {
    boardId: string;
    sections: { id: string, title: string }[];
};

type StatTileProps = {
    label: string;
    value: string;
    caption: string;
    valueColor?: string;
    trailing?: React.ReactNode;
};

function StatTile({label, value, caption, valueColor, trailing}: StatTileProps) {
    return (
        <Paper variant="outlined" sx={{p: 2.5, borderRadius: 2, minWidth: 0}}>
            <Typography variant="body2" color="text.secondary">{label}</Typography>
            <Box sx={{display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 1, my: 1}}>
                <Typography component="p" color={valueColor} sx={{fontSize: 36, fontWeight: 650, letterSpacing: '-0.04em', lineHeight: 1.2, fontVariantNumeric: 'tabular-nums'}}>{value}</Typography>
                {trailing}
            </Box>
            <Typography variant="caption" color="text.secondary">{caption}</Typography>
        </Paper>
    );
}

// How the board is doing: completion counts and rates over a recent period, plus where the work is
// (sections, recurring tasks, and — for owners and editors — people).
export function StatsView({boardId, sections}: StatsViewProps) {
    const [days, setDays] = React.useState(30);

    // Days are bucketed in the viewer's own time zone, so "completed today" matches their calendar.
    const timeZone = React.useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);

    const {data: stats, isLoading, isError} = useQuery<BoardStats>({
        queryKey: queryKeys.boardStats(boardId, days, timeZone),
        queryFn: () => {
            const to = dayjs();
            const from = to.subtract(days - 1, 'day').startOf('day');
            return getBoardStatsQuery(boardId, from.toISOString(), to.toISOString(), timeZone);
        },
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });

    const sectionTitle = (sectionId: string) => sections.find((s) => s.id === sectionId)?.title ?? 'Unknown section';

    const perDay = React.useMemo(() => (stats ? completionsPerDay(stats) : []), [stats]);

    const rangeToggle = (
        <ToggleButtonGroup
            exclusive
            size="small"
            value={days}
            onChange={(_event, value) => value && setDays(value)}
            aria-label="Time range"
        >
            {RANGES.map((range) => (
                <ToggleButton key={range.days} value={range.days}>{range.label}</ToggleButton>
            ))}
        </ToggleButtonGroup>
    );

    if (isError) {
        return <Alert severity="error">Couldn't load statistics. Try again.</Alert>;
    }

    if (isLoading || !stats) {
        return <Typography color="text.secondary">Loading…</Typography>;
    }

    const completionRate = percent(stats.due.completed, stats.due.total);
    const onTimeRate = percent(stats.onTime.onTime, stats.onTime.total);
    const change = stats.completed.current - stats.completed.previous;
    const maxPerDay = Math.max(1, ...perDay.map((row) => row.count));
    const maxPerson = Math.max(1, ...(stats.people ?? []).map((row) => row.count));
    const hasActivity = stats.completed.current > 0 || stats.due.total > 0 || stats.overdueNow > 0;

    return (
        <Stack spacing={3}>
            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap'}}>
                <Box>
                    <Typography variant="h6" component="h2">Task statistics</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {dayjs(stats.range.from).format('MMM D')} – {dayjs(stats.range.to).format('MMM D, YYYY')}
                    </Typography>
                </Box>
                {rangeToggle}
            </Box>

            {!hasActivity && (
                <Alert severity="info">
                    Nothing to show for this period yet. Completed tasks and tasks that came due will appear here.
                </Alert>
            )}

            <Box sx={{display: 'grid', gap: 2, gridTemplateColumns: {xs: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))'}}}>
                <StatTile
                    label="Completed"
                    value={String(stats.completed.current)}
                    caption={`${stats.completed.previous} in the previous ${days} days`}
                    trailing={
                        <Chip
                            size="small"
                            variant="outlined"
                            color={change > 0 ? 'success' : 'default'}
                            label={change > 0 ? `+${change}` : String(change)}
                        />
                    }
                />
                <StatTile
                    label="Completion rate"
                    value={completionRate === null ? '–' : `${completionRate}%`}
                    caption={`${stats.due.completed} of ${stats.due.total} tasks that came due are done`}
                />
                <StatTile
                    label="Completed on time"
                    value={onTimeRate === null ? '–' : `${onTimeRate}%`}
                    caption={`${stats.onTime.onTime} of ${stats.onTime.total} finished by their due time`}
                />
                <StatTile
                    label="Overdue now"
                    value={String(stats.overdueNow)}
                    caption={stats.overdueNow === 0 ? 'Nothing is past due' : 'Open tasks past their due time'}
                    valueColor={stats.overdueNow > 0 ? 'warning.main' : undefined}
                />
            </Box>

            <SettingsCard title="Completed per day" description="Tasks marked done each day.">
                <Box
                    role="img"
                    aria-label={`Bar chart of tasks completed per day over the last ${days} days`}
                    sx={{display: 'flex', alignItems: 'flex-end', gap: days > 30 ? '2px' : '4px', height: 140}}
                >
                    {perDay.map((row) => (
                        <Tooltip key={row.day} title={`${dayjs(row.day).format('ddd, MMM D')}: ${row.count} completed`}>
                            <Box sx={{flex: 1, minWidth: 0, height: '100%', display: 'flex', alignItems: 'flex-end'}}>
                                <Box
                                    sx={{
                                        width: '100%',
                                        height: row.count === 0 ? 3 : `${Math.max(6, (row.count / maxPerDay) * 100)}%`,
                                        bgcolor: row.count === 0 ? 'divider' : 'primary.main',
                                        borderRadius: '3px 3px 0 0',
                                    }}
                                />
                            </Box>
                        </Tooltip>
                    ))}
                </Box>
                <Box sx={{display: 'flex', justifyContent: 'space-between', mt: 0.5}}>
                    <Typography variant="caption" color="text.secondary">{dayjs(perDay[0]?.day).format('MMM D')}</Typography>
                    <Typography variant="caption" color="text.secondary">{dayjs(perDay[perDay.length - 1]?.day).format('MMM D')}</Typography>
                </Box>
            </SettingsCard>

            <Box sx={{display: 'grid', gap: 3, alignItems: 'start', gridTemplateColumns: {xs: 'minmax(0, 1fr)', lg: 'repeat(2, minmax(0, 1fr))'}}}>
                <SettingsCard title="By section" description="Tasks that came due in this period, and how many are done.">
                    {stats.sections.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">No tasks came due in this period.</Typography>
                    ) : (
                        <Stack spacing={2}>
                            {stats.sections.map((row) => (
                                <Box key={row.sectionId}>
                                    <Box sx={{display: 'flex', justifyContent: 'space-between', gap: 2}}>
                                        <Typography variant="body2" fontWeight={600} noWrap>{sectionTitle(row.sectionId)}</Typography>
                                        <Typography variant="body2" color="text.secondary">{row.completed} / {row.total}</Typography>
                                    </Box>
                                    <LinearProgress
                                        variant="determinate"
                                        value={(row.completed / row.total) * 100}
                                        color="success"
                                        sx={{height: 6, borderRadius: 3, mt: 0.5}}
                                    />
                                </Box>
                            ))}
                        </Stack>
                    )}
                </SettingsCard>

                <SettingsCard title="Recurring tasks" description="How often each series gets done, and the current streak of finished occurrences in a row.">
                    {stats.series.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">No recurring tasks came due in this period.</Typography>
                    ) : (
                        <Stack spacing={2}>
                            {stats.series.map((row) => (
                                <Box key={row.seriesId}>
                                    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2}}>
                                        <Typography variant="body2" fontWeight={600} noWrap>{row.title}</Typography>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            {row.streak > 1 && (
                                                <Tooltip title={`${row.streak} occurrences in a row`}>
                                                    <Chip
                                                        size="small"
                                                        color="warning"
                                                        variant="outlined"
                                                        icon={<LocalFireDepartmentIcon/>}
                                                        label={row.streak}
                                                    />
                                                </Tooltip>
                                            )}
                                            <Typography variant="body2" color="text.secondary">{row.completed} / {row.total}</Typography>
                                        </Stack>
                                    </Box>
                                    <LinearProgress
                                        variant="determinate"
                                        value={(row.completed / row.total) * 100}
                                        color="success"
                                        sx={{height: 6, borderRadius: 3, mt: 0.5}}
                                    />
                                </Box>
                            ))}
                        </Stack>
                    )}
                </SettingsCard>
            </Box>

            {stats.people && (
                <SettingsCard title="By person" description="Who completed tasks in this period. Only owners and editors can see this.">
                    {stats.people.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">No completions in this period.</Typography>
                    ) : (
                        <Stack spacing={2}>
                            {stats.people.map((row) => (
                                <Box key={row.userId ?? 'unknown'}>
                                    <Box sx={{display: 'flex', justifyContent: 'space-between', gap: 2}}>
                                        <Typography variant="body2" fontWeight={600} noWrap>{row.name}</Typography>
                                        <Typography variant="body2" color="text.secondary">{row.count}</Typography>
                                    </Box>
                                    <LinearProgress
                                        variant="determinate"
                                        value={(row.count / maxPerson) * 100}
                                        sx={{height: 6, borderRadius: 3, mt: 0.5}}
                                    />
                                </Box>
                            ))}
                        </Stack>
                    )}
                </SettingsCard>
            )}
        </Stack>
    );
}
